<template>
  <div class="text-input-container">
    <label v-if="label" class="text-input-label" :for="inputId">{{ label }}<span v-if="required" class="required-indicator">*</span><vue-feather v-if="tooltip" type="info" size="14" class="label-tooltip-icon" v-tooltip="tooltip" /></label>
    <div class="text-input" :class="inputClass">
      <transition name="ai-prefill" mode="out-in">
        <input
          v-if="!aiPrefill"
          key="input"
          :id="inputId"
          :type="inputType"
          class="text-input-field"
          :placeholder="computedPlaceholder"
          :value="modelValue"
          :disabled="disabled"
          :inputmode="inputMode"
          :maxlength="maxLength"
          autocomplete="nope"
          data-lpignore="true"
          data-form-type="other"
          @input="handleInput"
          @focus="isFocused = true"
          @blur="handleBlur"
          @keydown="handleKeydown"
          @paste="handlePaste"
          ref="input"
        >
        <!-- AI Prefill chip overlay -->
        <div v-else class="ai-prefill-chip" key="prefill">
          <div class="ai-prefill-chip-content">
            <img src="@/assets/icons/stars.png" alt="AI" class="ai-prefill-icon" />
            <span class="ai-prefill-value">{{ aiPrefill.value }}</span>
          </div>
          <button
            class="ai-prefill-dismiss"
            @click="dismissPrefill"
            type="button"
            title="Dismiss AI suggestion"
          >
            <vue-feather type="x" size="14" />
          </button>
        </div>
      </transition>
    </div>
    <span v-if="error" class="text-input-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

let inputId = 0

const FORMAT_PLACEHOLDERS = {
  phone: '(555) 123-4567',
  date: 'MM/DD/YYYY',
  email: 'email@example.com'
}

export default {
  name: 'TextInput',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    // Format: 'text', 'phone', 'date', 'email'
    format: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'phone', 'date', 'email'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      default: undefined
    },
    tooltip: {
      type: String,
      default: ''
    },
    // AI prefill configuration: { value: String }
    // The icon is always the AI stars icon
    aiPrefill: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      inputId: `text-input-${++inputId}`,
      isFocused: false
    }
  },
  computed: {
    inputClass() {
      return {
        'text-input--focused': this.isFocused,
        'text-input--disabled': this.disabled,
        'text-input--error': this.error,
        'text-input--prefill': !!this.aiPrefill
      }
    },
    computedPlaceholder() {
      if (this.placeholder) return this.placeholder
      return FORMAT_PLACEHOLDERS[this.format] || ''
    },
    inputType() {
      if (this.format === 'email') return 'email'
      return 'text'
    },
    inputMode() {
      if (this.format === 'phone' || this.format === 'date') return 'numeric'
      if (this.format === 'email') return 'email'
      return 'text'
    },
    maxLength() {
      if (this.maxlength !== undefined) return this.maxlength
      if (this.format === 'phone') return 14  // (XXX) XXX-XXXX
      if (this.format === 'date') return 10   // MM/DD/YYYY
      return undefined
    }
  },
  methods: {
    handleInput(event) {
      // If there's an AI prefill, dismiss it when user starts typing
      if (this.aiPrefill) {
        this.$emit('dismiss-prefill')
        return
      }

      const input = event.target
      const oldValue = this.modelValue || ''
      let newValue = input.value
      let cursorPos = input.selectionStart

      if (this.format === 'phone') {
        const result = this.formatPhone(newValue, oldValue, cursorPos)
        newValue = result.value
        cursorPos = result.cursor
      } else if (this.format === 'date') {
        const result = this.formatDate(newValue, oldValue, cursorPos)
        newValue = result.value
        cursorPos = result.cursor
      }

      this.$emit('update:modelValue', newValue)

      // Restore cursor position after Vue updates the input
      if (this.format === 'phone' || this.format === 'date') {
        this.$nextTick(() => {
          input.setSelectionRange(cursorPos, cursorPos)
        })
      }
    },

    handlePaste(event) {
      // For phone and date formats, filter pasted content to only digits
      if (this.format === 'phone' || this.format === 'date') {
        event.preventDefault()
        const paste = (event.clipboardData || window.clipboardData).getData('text')
        const digitsOnly = paste.replace(/\D/g, '')

        const input = this.$refs.input
        const start = input.selectionStart
        const end = input.selectionEnd
        const currentValue = this.modelValue || ''

        // Insert digits at cursor position
        const newValue = currentValue.slice(0, start) + digitsOnly + currentValue.slice(end)

        if (this.format === 'phone') {
          const result = this.formatPhone(newValue, currentValue, start + digitsOnly.length)
          this.$emit('update:modelValue', result.value)
          this.$nextTick(() => {
            input.setSelectionRange(result.cursor, result.cursor)
          })
        } else if (this.format === 'date') {
          const result = this.formatDate(newValue, currentValue, start + digitsOnly.length)
          this.$emit('update:modelValue', result.value)
          this.$nextTick(() => {
            input.setSelectionRange(result.cursor, result.cursor)
          })
        }
      }
    },

    handleKeydown(event) {
      // For phone and date formats, only allow digits and control keys
      if (this.format === 'phone' || this.format === 'date') {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End']
        const isDigit = /^\d$/.test(event.key)
        const isAllowedKey = allowedKeys.includes(event.key)
        const hasModifier = event.ctrlKey || event.metaKey || event.altKey

        if (!isDigit && !isAllowedKey && !hasModifier) {
          event.preventDefault()
          return
        }
      }

      // For date format, allow backspace to skip over slashes
      if (this.format === 'date' && event.key === 'Backspace') {
        const input = this.$refs.input
        const pos = input.selectionStart
        const value = this.modelValue || ''

        // If cursor is right after a slash, skip over it
        if (pos > 0 && value[pos - 1] === '/') {
          event.preventDefault()
          const newValue = value.slice(0, pos - 2) + value.slice(pos)
          this.$emit('update:modelValue', newValue)
          this.$nextTick(() => {
            input.setSelectionRange(pos - 2, pos - 2)
          })
        }
      }

      // For phone format, allow backspace to skip over formatting characters
      if (this.format === 'phone' && event.key === 'Backspace') {
        const input = this.$refs.input
        const pos = input.selectionStart
        const value = this.modelValue || ''

        // If cursor is right after a non-digit, skip over it
        if (pos > 0 && /\D/.test(value[pos - 1])) {
          event.preventDefault()
          // Find the previous digit position
          let newPos = pos - 1
          while (newPos > 0 && /\D/.test(value[newPos - 1])) {
            newPos--
          }
          if (newPos > 0) {
            const newValue = value.slice(0, newPos - 1) + value.slice(pos)
            this.$emit('update:modelValue', this.formatPhone(newValue, value, newPos - 1).value)
            this.$nextTick(() => {
              input.setSelectionRange(newPos - 1, newPos - 1)
            })
          }
        }
      }
    },

    formatPhone(value, _oldValue, cursorPos) {
      // Extract only digits, strictly limit to 10
      let digits = value.replace(/\D/g, '')
      if (digits.length > 10) {
        digits = digits.slice(0, 10)
      }

      // Count digits before cursor in the raw input (capped at 10)
      const rawBeforeCursor = value.slice(0, cursorPos)
      const digitsBeforeCursor = Math.min(rawBeforeCursor.replace(/\D/g, '').length, 10)

      // Format: (XXX) XXX-XXXX
      let formatted = ''
      if (digits.length > 0) {
        formatted = '('
        formatted += digits.slice(0, 3)
        if (digits.length >= 3) {
          formatted += ') '
          formatted += digits.slice(3, 6)
          if (digits.length >= 6) {
            formatted += '-'
            formatted += digits.slice(6, 10)
          }
        }
      }

      // Calculate new cursor position
      let newCursor = 0
      let digitCount = 0
      for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
        newCursor = i + 1
        if (/\d/.test(formatted[i])) {
          digitCount++
        }
      }

      // Adjust cursor for formatting characters that come right after
      if (newCursor < formatted.length) {
        while (newCursor < formatted.length && !/\d/.test(formatted[newCursor])) {
          newCursor++
        }
      }

      return { value: formatted, cursor: newCursor }
    },

    formatDate(value, _oldValue, cursorPos) {
      // Extract only digits
      const digits = value.replace(/\D/g, '').slice(0, 8)

      // Count digits before cursor in the raw input
      const rawBeforeCursor = value.slice(0, cursorPos)
      const digitsBeforeCursor = rawBeforeCursor.replace(/\D/g, '').length

      // Format: MM/DD/YYYY
      let formatted = ''
      if (digits.length > 0) {
        formatted += digits.slice(0, 2)
        if (digits.length >= 2) {
          formatted += '/'
          formatted += digits.slice(2, 4)
          if (digits.length >= 4) {
            formatted += '/'
            formatted += digits.slice(4, 8)
          }
        }
      }

      // Calculate new cursor position
      let newCursor = 0
      let digitCount = 0
      for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
        newCursor = i + 1
        if (/\d/.test(formatted[i])) {
          digitCount++
        }
      }

      // Adjust cursor for formatting characters that come right after
      if (newCursor < formatted.length && formatted[newCursor] === '/') {
        newCursor++
      }

      return { value: formatted, cursor: newCursor }
    },

    handleBlur() {
      this.isFocused = false
      this.$emit('blur')
    },

    focus() {
      this.$refs.input.focus()
    },

    // Get raw value (digits only) for phone/date
    getRawValue() {
      if (this.format === 'phone' || this.format === 'date') {
        return (this.modelValue || '').replace(/\D/g, '')
      }
      return this.modelValue || ''
    },

    // Check if the current value is valid for the format
    isValid() {
      const value = this.modelValue || ''
      if (!value) return false

      if (this.format === 'phone') {
        return /^\(\d{3}\) \d{3}-\d{4}$/.test(value)
      }
      if (this.format === 'date') {
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false
        const [month, day, year] = value.split('/').map(Number)
        if (month < 1 || month > 12) return false
        if (day < 1 || day > 31) return false
        if (year < 1900 || year > 2099) return false
        return true
      }
      if (this.format === 'email') {
        return /^.+@.+\..+$/.test(value)
      }
      return value.trim().length > 0
    },

    dismissPrefill() {
      this.$emit('dismiss-prefill')
    }
  },
  emits: ['update:modelValue', 'blur', 'dismiss-prefill']
}
</script>

<style scoped>
.text-input-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.text-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-tooltip-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.required-indicator {
  color: #dc2626;
  margin-left: 2px;
}

.text-input {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.text-input:hover:not(.text-input--disabled):not(.text-input--error) {
  border-color: #9ca3af;
}

.text-input--focused:not(.text-input--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.text-input--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.text-input--error {
  border-color: #dc2626;
}

.text-input--error.text-input--focused {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

/* AI Prefill chip styling */
.text-input--prefill {
  background: transparent;
  border-color: transparent;
  padding: 0;
}

/* AI Prefill transition */
.ai-prefill-enter-active {
  animation: ai-prefill-in 0.25s ease-out;
}

.ai-prefill-leave-active {
  animation: ai-prefill-out 0.2s ease-in;
}

@keyframes ai-prefill-in {
  0% {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes ai-prefill-out {
  0% {
    opacity: 1;
  }
  40% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes ai-prefill-border-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.ai-prefill-chip.ai-prefill-leave-active::before {
  animation: ai-prefill-border-out 0.08s ease-out forwards;
}

.ai-prefill-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: 45px;
  padding: 0 6px 0 14px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(168, 85, 247, 0.08) 100%);
  border: 2px solid transparent;
  border-radius: 4px;
  background-clip: padding-box;
  position: relative;
  box-sizing: border-box;
}

.ai-prefill-chip::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 6px;
  padding: 2px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #a855f7 100%);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.ai-prefill-chip-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.ai-prefill-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  filter: drop-shadow(0 0 4px rgba(139, 92, 246, 0.4));
}

.ai-prefill-value {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #4c1d95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-prefill-dismiss {
  width: 28px;
  height: 28px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.ai-prefill-dismiss:hover {
  background: rgba(139, 92, 246, 0.2);
  border-color: rgba(139, 92, 246, 0.4);
  color: #6d28d9;
  transform: scale(1.05);
}

.ai-prefill-dismiss:active {
  transform: scale(0.95);
}

.text-input-field {
  width: 100%;
  height: 100%;
  padding: 0 14px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
}

.text-input-field::placeholder {
  color: #9ca3af;
}

.text-input-field:disabled {
  cursor: not-allowed;
}

.text-input-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
